import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { analytics } from "@heycatch/sdk";
import api, { formatApiErrorDetail, tokenStore } from "@/lib/api";

const AuthContext = createContext(null);

/**
 * Identify a user to HeyCatch. Uses the app's stable user id (never email
 * or a session token) and puts email/name/plan into properties so the
 * dashboard renders "Name <email>" instead of an opaque id.
 */
function identifyToHeyCatch(user) {
    if (!user || !user.id) return;
    try {
        const plan = user.subscription_status === "active" ? "pro" : "free";
        analytics.setIdentity(
            user.id,
            { email: user.email, name: user.name, plan },
            { signup_date: user.created_at || undefined },
        );
    } catch (e) {
        // Never let analytics break auth
    }
}

export function AuthProvider({ children }) {
    // undefined = checking, null = logged out, object = logged in
    const [user, setUser] = useState(undefined);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            identifyToHeyCatch(data);
            return data;
        } catch (e) {
            setUser(null);
            return null;
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // If the axios refresh interceptor exhausts (refresh_token also dead),
    // flush user state so ProtectedRoute redirects to sign-in — otherwise
    // pages hang forever in their "Loading…" states.
    useEffect(() => {
        const onExpired = () => setUser(null);
        window.addEventListener("atlas:auth-expired", onExpired);
        return () => window.removeEventListener("atlas:auth-expired", onExpired);
    }, []);

    const login = async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            tokenStore.save(data);
            setUser(data);
            identifyToHeyCatch(data);
            return { ok: true, user: data };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
        }
    };

    const register = async (email, password, name, orgFields = null, referralCode = null) => {
        try {
            const payload = { email, password, name };
            if (orgFields && orgFields.as_organization) {
                Object.assign(payload, {
                    as_organization: true,
                    org_name: orgFields.org_name,
                    org_domain: orgFields.org_domain,
                    org_website: orgFields.org_website || null,
                });
            }
            if (referralCode) payload.referral_code = referralCode;
            const { data } = await api.post("/auth/register", payload);
            tokenStore.save(data);
            setUser(data);
            identifyToHeyCatch(data);
            return { ok: true, user: data };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
        }
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch {}
        tokenStore.clear();
        setUser(null);
        try {
            analytics.resetIdentity();
        } catch {}
    };

    return (
        <AuthContext.Provider value={{ user, setUser, refresh, login, register, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
